import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Card, PixelPlaceholderLoader, ProgressBar } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import type { IWidgetModelTypesWebSource } from "app/components/widget/widget-types/Widget";
import { percentageSignFilter } from "app/filters/ngFilters";
import type { ITrafficAndEngagementWidgetProps } from "app/pages/website-analysis/TrafficAndEngagement/types";

import { CompareCardHeader } from "../common/CompareCardHeader";
import { DOMAIN_COLUMN_MIN_WIDTH } from "../common/constants";
import { DomainWithTooltipLink } from "../common/DomainWithTooltipLink";
import { EmptyStatePlaceholder } from "../common/EmptyStatePlaceholder";
import { useSubtitleFilterParams } from "../common/useSubtitleFilterParams";

const TABLE_METRIC_NAME = "EngagementDesktopVsMobileVisits";
const VALUE_COLUMN_WIDTH = "40px";

const formatPercent = percentageSignFilter();

interface IDeviceDistributionRow {
    domain: string;
    favicon: string;
    desktop: number;
    mobile: number;
}

export const DeviceDistribution = ({
    subtitleFilters,
    data,
    hasGA,
    isLoading,
    params,
    showA2d = true,
}: ITrafficAndEngagementWidgetProps) => {
    const { translate } = useTranslate();

    const { countryCode, countryText, dateRangeText } = useSubtitleFilterParams(subtitleFilters);

    const safeParams = (params ?? {}) as { webSource?: IWidgetModelTypesWebSource };

    const rows = useMemo<IDeviceDistributionRow[]>(() => {
        const source = (data as Record<string, unknown> | undefined)?.Data;
        const rawData = Array.isArray(source) ? source : [];

        return rawData.map((item) => {
            const entry = item as Record<string, unknown>;
            const shareVisits = Array.isArray(entry.DesktopMobileShareVisits)
                ? (entry.DesktopMobileShareVisits as unknown[])
                : [];

            return {
                domain: typeof entry.Domain === "string" ? entry.Domain : "",
                favicon: typeof entry.Favicon === "string" ? entry.Favicon : "",
                desktop: Number(shareVisits[0] ?? 0),
                mobile: Number(shareVisits[1] ?? 0),
            };
        });
    }, [data]);

    const isEmpty = rows.length === 0;

    if (isLoading) {
        return (
            <Card data-automation="device-distribution-compare-widget">
                <Flex
                    flexDirection="column"
                    padding="16px 24px"
                    gap="16px"
                    data-automation="device-distribution-loading"
                >
                    <PixelPlaceholderLoader width="40%" height={16} />
                    <PixelPlaceholderLoader width="100%" height={20} />
                    <PixelPlaceholderLoader width="100%" height={20} />
                </Flex>
            </Card>
        );
    }

    if (isEmpty) {
        return (
            <Card data-automation="device-distribution-compare-widget">
                <CompareCardHeader
                    title={translate("wa.ao.trafficshare")}
                    tooltipText={translate("wa.ao.trafficshare.tooltip")}
                    dateRangeText={dateRangeText}
                    countryCode={countryCode}
                    countryText={countryText}
                    dataAutomation="compare-device-distribution"
                />
                <EmptyStatePlaceholder />
            </Card>
        );
    }

    return (
        <Card data-automation="device-distribution-compare-widget">
            <CompareCardHeader
                title={translate("wa.ao.trafficshare")}
                tooltipText={translate("wa.ao.trafficshare.tooltip")}
                dateRangeText={dateRangeText}
                countryCode={countryCode}
                countryText={countryText}
                dataAutomation="compare-device-distribution"
                headerAction={
                    showA2d ? (
                        <AddToDashboard
                            webSource={safeParams.webSource}
                            type="Table"
                            metric={TABLE_METRIC_NAME}
                            modelType="fromWebsite"
                            filters={{
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                ShouldGetVerifiedData: hasGA,
                            }}
                        />
                    ) : null
                }
            />
            <Flex
                flexDirection="column"
                gap="16px"
                padding="16px 24px"
                flex="1"
                data-automation="device-distribution-table"
            >
                <Flex
                    alignItems="center"
                    gap="12px"
                    data-automation="device-distribution-table-header"
                >
                    <Flex
                        flex="2"
                        minWidth={DOMAIN_COLUMN_MIN_WIDTH}
                    >
                        <Text
                            fontVariant="captionTitle"
                            color={colorsPalettes.carbon[300]}
                        >
                            {translate("global.domain")}
                        </Text>
                    </Flex>
                    <Flex
                        flex="3"
                        minWidth="0"
                        alignItems="center"
                        gap="12px"
                    >
                        <Text
                            fontVariant="captionTitle"
                            color={colorsPalettes.carbon[300]}
                        >
                            {translate("websources.desktop")}
                        </Text>
                        <Flex
                            flex="1"
                            minWidth="0"
                        />
                        <Text
                            fontVariant="captionTitle"
                            color={colorsPalettes.carbon[300]}
                            width={VALUE_COLUMN_WIDTH}
                            maxWidth={VALUE_COLUMN_WIDTH}
                            textAlign="right"
                        >
                            {translate("analysis.audience.mobileweb.mobile")}
                        </Text>
                    </Flex>
                </Flex>
                {rows.map((row) => (
                    <Flex
                        key={row.domain}
                        alignItems="center"
                        gap="12px"
                        data-automation="device-distribution-row"
                    >
                        <Flex
                            flex="2"
                            minWidth={DOMAIN_COLUMN_MIN_WIDTH}
                            alignItems="center"
                        >
                            <DomainWithTooltipLink
                                domain={row.domain}
                                favicon={row.favicon ?? ""}
                            />
                        </Flex>
                        <Flex
                            flex="3"
                            minWidth="0"
                            alignItems="center"
                            gap="12px"
                        >
                            <Text
                                fontVariant="input2"
                                color={colorsPalettes.carbon[400]}
                                data-automation="device-distribution-desktop-value"
                            >
                                {formatPercent(row.desktop, 1)}
                            </Text>
                            <Flex
                                flex="1"
                                minWidth="0"
                            >
                                <ProgressBar
                                    values={[row.desktop, row.mobile]}
                                    colorSet={[colorsPalettes.blue[400], colorsPalettes.blue[200]]}
                                />
                            </Flex>
                            <Flex
                                width={VALUE_COLUMN_WIDTH}
                                maxWidth={VALUE_COLUMN_WIDTH}
                                justifyContent="flex-end"
                            >
                                <Text
                                    fontVariant="input2"
                                    color={colorsPalettes.carbon[400]}
                                    textAlign="right"
                                    data-automation="device-distribution-mobile-value"
                                >
                                    {formatPercent(row.mobile, 1)}
                                </Text>
                            </Flex>
                        </Flex>
                    </Flex>
                ))}
            </Flex>
        </Card>
    );
};
